import React, { useState, useEffect } from 'react';
import { Typography, Form, Input, InputNumber, Select, DatePicker, Button, Upload, Row, Col, message, Modal } from 'antd';
import { UploadOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import DOLayout from '../../components/directOrders/DOLayout';
import ApprovalTrack from '../../components/directOrders/ApprovalTrack';
import { approverAt, firstName, useApprovers } from '../../utils/approverStore';
import OrdersExplorer from '../../components/directOrders/OrdersExplorer';
import SupplierHistory from '../../components/directOrders/SupplierHistory';
import { useAuth } from '../../context/AuthContext';
import { useDirectOrder } from '../../context/DirectOrderContext';
import lineVotesService from '../../services/lineVotesService';
import { formatEuro } from '../../utils/directOrderStatus';
import dayjs from 'dayjs';
import { API_ORIGIN } from '../../config';
import { LIMIT_LABEL, totalLimitRule } from '../../utils/directOrderLimits';

const { Text } = Typography;
const { TextArea } = Input;

const countWords = (value) => (value ? value.trim().split(/\s+/).filter((word) => word.length > 0).length : 0);

const Section = ({ title, hint, children }) => (
  <section className="do-form-section">
    <div>
      <h2 className="do-form-section__title">{title}</h2>
      {hint && <p className="do-form-section__hint">{hint}</p>}
    </div>
    <div>{children}</div>
  </section>
);

const CreateDirectOrder = () => {
  const { user } = useAuth();
  useApprovers(Boolean(user));
  const { createDirectOrder, loading, loadUserDirectOrders, userDirectOrders } = useDirectOrder();
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [previousOrdersModal, setPreviousOrdersModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Dropdown data fetched from the API
  const [initiatives, setInitiatives] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [lineVotes, setLineVotes] = useState([]);

  const totalCost = Form.useWatch('totalCost', form);
  const supplierName = Form.useWatch('supplierName', form);
  const mainLineVote = Form.useWatch('lineVote', form);

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/initiatives`, { credentials: 'include' });
        const data = await response.json();

        if (data.success && Array.isArray(data.initiatives)) {
          const options = data.initiatives
            .filter((item) => item && item.id != null && item.initiative)
            .sort((a, b) => a.id - b.id)
            .map((item) => ({ value: String(item.id), label: item.initiative }));
          setInitiatives(options);
        }
      } catch (error) {
        message.error('Failed to load initiatives');
      }
    };

    const fetchJustifications = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/justifications`, { credentials: 'include' });
        const data = await response.json();

        if (data.success && Array.isArray(data.justifications)) {
          const options = data.justifications
            .filter((item) => item && item.justificationId != null && item.justification)
            .sort((a, b) => a.justificationId - b.justificationId)
            .map((item) => ({ value: String(item.justificationId), label: item.justification }));
          setJustifications(options);
        }
      } catch (error) {
        message.error('Failed to load justifications');
      }
    };

    const fetchLineVotes = async () => {
      try {
        const response = await lineVotesService.getAll();
        if (response.success && Array.isArray(response.lineVotes)) {
          const options = response.lineVotes
            .filter((item) => item && item.id != null)
            .map((item) => ({ value: item.id, label: `${item.LineVote} - ${item.Festival}` }));
          setLineVotes(options);
        }
      } catch (error) {
        message.error('Failed to load line votes');
      }
    };

    fetchInitiatives();
    fetchJustifications();
    fetchLineVotes();
  }, []);

  const userDefaults = () => ({
    yourEmail: user.email,
    officerName: `${user.firstName || user.first_name || user.fName || ''} ${user.lastName || user.last_name || user.lName || ''}`.trim(),
    orderDate: dayjs(),
    entityDept: 'Festivals Malta Agency',
    approver: 'Francis Agius', // Fixed approver
  });

  // Prefill form with user data
  useEffect(() => {
    if (user) {
      form.setFieldsValue(userDefaults());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, form]);

  const handleSubmit = async (values) => {
    try {
      const directOrderData = {
        ...values,
        orderDate: values.orderDate.format('YYYY-MM-DD'),
        approver: 'Francis Agius', // Fixed approver
        doEntity: 1, // numeric entity id (Festivals Malta Agency)
      };

      // FormData sends strings, so convert numeric fields for the backend
      if (directOrderData.totalCost !== undefined && directOrderData.totalCost !== null) {
        directOrderData.totalCost = directOrderData.totalCost.toString();
      }

      if (values.contractTimeframe) {
        directOrderData.contractTimeframe = values.contractTimeframe.format('DD/MM/YYYY');
      }

      // Backend expects the initiative as an integer ID
      if (directOrderData.initiative !== undefined && directOrderData.initiative !== null) {
        directOrderData.initiative = parseInt(directOrderData.initiative, 10);
      }

      // Backend expects justificationId as an integer
      if (values.justification !== undefined && values.justification !== null) {
        directOrderData.justificationId = parseInt(values.justification, 10);
        delete directOrderData.justification;
      }

      if (values.detailedJustification) {
        directOrderData.doDetailedJustification = values.detailedJustification;
        delete directOrderData.detailedJustification;
      }

      if (values.lineVote !== undefined && values.lineVote !== null) {
        directOrderData.lineVoteId = parseInt(values.lineVote, 10);
        delete directOrderData.lineVote;
      }

      // FormData sends text, so the list goes as JSON, e.g. "[12,15]"
      const additionalLineVotes = (values.additionalLineVotes || []).map((id) => parseInt(id, 10));
      delete directOrderData.additionalLineVotes;
      if (additionalLineVotes.length > 0) {
        directOrderData.additionalLineVoteIds = JSON.stringify(additionalLineVotes);
      }

      // Backend requires entityDept
      if (!directOrderData.entityDept) {
        directOrderData.entityDept = 'Festivals Malta Agency';
      }

      const result = await createDirectOrder(directOrderData, attachments);

      if (result && result.success === false) {
        message.error(result.message || 'The direct order could not be submitted. Check the form and try again.', 8);
        return;
      }
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (error) {
      message.error(`${error.message || 'Failed to submit direct order'}. Check the form and try again.`, 8);
    }
  };

  const startAnother = () => {
    form.resetFields();
    setAttachments([]);
    form.setFieldsValue(userDefaults());
    setSubmitted(false);
  };

  const handleFileUpload = ({ fileList }) => {
    setAttachments(fileList.map((file) => file.originFileObj));
  };

  const handleInitiativeChange = (value) => {
    form.setFieldsValue({ otherInitiative: value === '999' ? '' : undefined });
  };

  const handleUsePreviousOrder = () => {
    loadUserDirectOrders();
    setPreviousOrdersModal(true);
  };

  const handleSelectPreviousOrder = (order) => {
    const findInitiativeValue = (label) => {
      const initiative = initiatives.find((init) => init.label === label);
      return initiative ? initiative.value : '';
    };

    const findJustificationValue = (label) => {
      const justification = justifications.find((just) => just.label === label);
      return justification ? justification.value : '';
    };

    const parseContractTimeframe = (timeframeStr) => {
      if (!timeframeStr) return undefined;
      try {
        const date = dayjs(timeframeStr, 'DD/MM/YYYY');
        return date.isValid() ? date : undefined;
      } catch (e) {
        return undefined;
      }
    };

    // Line votes that have since been deleted can't be picked again, so they aren't copied
    const activeLineVotes = new Set(lineVotes.map((option) => option.value));
    const copyLineVote = (id) => (activeLineVotes.has(id) ? id : undefined);

    // Copy the previous order's content; user details, date and attachments stay current
    form.setFieldsValue({
      ...userDefaults(),
      ccEmail: order.doCCEmail || '',
      initiative: findInitiativeValue(order.doInitiativeName),
      otherInitiative: order.doOtherInitiative || '',
      justification: findJustificationValue(order.doJustificationName),
      detailedJustification: order.doDetailedJustification || '',
      supplierName: order.doSupplier || '',
      briefDesc1: order.doNameBriefDesc || '',
      breakdown1: order.doBreakdown || '',
      contractTimeframe: parseContractTimeframe(order.doTimeframe),
      costPerItem: order.doCostPerItem || '',
      totalCost: order.doTotalCost || '',
      callForQuotation: order.doCallForQuiteOrInterest || '',
      paymentTerms: order.doTermsOfPayment || '',
      referenceNo: order.doRefNoAndDateOfWiningBidEtc || '',
      fundsAvailable: order.doFundsAvailability || '',
      history: order.doBriefHistory || '',
      lineVote: copyLineVote(order.lineVoteId),
      additionalLineVotes: (order.additionalLineVotes || []).map((lv) => copyLineVote(lv.id)).filter(Boolean),
    });

    setPreviousOrdersModal(false);
    message.success(`Form filled from direct order #${order.doId}`);
  };

  const totalLabel = totalCost !== undefined && totalCost !== null && totalCost !== '' ? formatEuro(totalCost) : '€0.00';

  const previousOrdersButton = (
    <Button icon={<CopyOutlined />} onClick={handleUsePreviousOrder} disabled={submitted}>
      Start from a previous order
    </Button>
  );

  if (submitted) {
    return (
      <DOLayout title="New direct order" back={{ to: '/direct-order', label: 'Direct orders' }} showNav={false}>
        <div className="do-success" role="status">
          <h2>Direct order submitted</h2>
          <p>
            It is now with {approverAt(1) ? firstName(approverAt(1)) : 'the first approver'} for the first review. You can
            follow it through approval under My orders.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/direct-order/view">
              <Button type="primary">View my orders</Button>
            </Link>
            <Button onClick={startAnother}>Raise another</Button>
          </div>
        </div>
      </DOLayout>
    );
  }

  return (
    <DOLayout
      title="New direct order"
      subtitle="Fields marked optional can be left blank. Every other field is needed before the order can be submitted."
      back={{ to: '/direct-order', label: 'Direct orders' }}
      actions={previousOrdersButton}
      showNav={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      >
        <div className="do-create">
          <div>
            <Section title="Requestor" hint="Filled in from your account.">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="yourEmail" label="Your email" rules={[{ required: true, message: 'Email is required' }]}>
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="officerName"
                    label="Officer raising the request"
                    rules={[{ required: true, message: 'Officer name is required' }]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="ccEmail"
                    label="Copy to"
                    rules={[{ type: 'email', message: 'Enter a valid email address' }]}
                  >
                    <Input placeholder="colleague@festivals.mt" />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Order" hint="What the order is for, where the money comes from, and why it skips a tender.">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="orderDate" label="Date" rules={[{ required: true, message: 'Date is required' }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="entityDept" label="Entity and department" initialValue="Festivals Malta Agency" required>
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="initiative" label="Initiative" rules={[{ required: true, message: 'Initiative is required' }]}>
                    <Select
                      placeholder="Select an initiative"
                      onChange={handleInitiativeChange}
                      showSearch
                      optionFilterProp="label"
                      options={initiatives}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="lineVote" label="Line vote" rules={[{ required: true, message: 'Select a line vote' }]}>
                    <Select
                      placeholder="Select a line vote"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      options={lineVotes}
                      onChange={(value) => {
                        // The main line vote can't also be an additional one
                        const additional = form.getFieldValue('additionalLineVotes') || [];
                        form.setFieldsValue({ additionalLineVotes: additional.filter((id) => id !== value) });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.initiative !== curr.initiative}>
                  {({ getFieldValue }) =>
                    getFieldValue('initiative') === '999' ? (
                      <Col xs={24}>
                        <Form.Item
                          name="otherInitiative"
                          label="Name of the other initiative"
                          rules={[{ required: true, message: 'Enter the initiative name' }]}
                        >
                          <Input placeholder="Initiative name" />
                        </Form.Item>
                      </Col>
                    ) : null
                  }
                </Form.Item>
                <Col xs={24}>
                  <Form.Item
                    name="additionalLineVotes"
                    label="Additional line votes"
                    extra="If the order is charged to more than one line vote, pick the others here."
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select any other line votes"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      options={lineVotes.filter((option) => option.value !== mainLineVote)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="justification"
                    label="Justification (procurement policy note 32, section 1)"
                    rules={[{ required: true, message: 'Justification is required' }]}
                  >
                    <Select placeholder="Select a justification" showSearch optionFilterProp="label" options={justifications} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="detailedJustification"
                    label="Detailed justification"
                    rules={[
                      { required: true, message: 'Detailed justification is required' },
                      {
                        validator: (_, value) => {
                          const words = countWords(value);
                          return words > 500
                            ? Promise.reject(new Error(`Maximum 500 words allowed (current: ${words} words)`))
                            : Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <TextArea
                      rows={5}
                      placeholder="Explain why this has to be a direct order (500 words at most)"
                      showCount={{ formatter: ({ value }) => `${countWords(value)} / 500 words` }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Supplier and items">
              <Form.Item name="supplierName" label="Supplier" rules={[{ required: true, message: 'Supplier name is required' }]}>
                <Input placeholder="Supplier name" />
              </Form.Item>
              <SupplierHistory supplier={supplierName} />
              <Form.Item
                name="briefDesc1"
                label="Name and brief description"
                rules={[{ required: true, message: 'Name and brief description is required' }]}
              >
                <TextArea rows={3} placeholder="What is being bought" />
              </Form.Item>
              <Form.Item
                name="breakdown1"
                label="Breakdown of works, supplies or services"
                rules={[{ required: true, message: 'Breakdown is required' }]}
              >
                <TextArea rows={3} placeholder="Itemised breakdown" />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="costPerItem"
                    label="Cost per item or hourly rate (excl. VAT)"
                    rules={[{ required: true, message: 'Cost per item is required' }]}
                  >
                    <Input placeholder="0.00" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="totalCost"
                    label="Total cost (excl. VAT)"
                    extra={`Up to ${LIMIT_LABEL}. Direct orders above that can't be submitted.`}
                    rules={[{ required: true, message: 'Total cost is required' }, totalLimitRule]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      placeholder="0.00"
                      controls={false}
                      prefix="€"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="contractTimeframe"
                    label="Contract time frame"
                    rules={[{ required: true, message: 'Contract time frame is required' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select a date" />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Procurement and funding">
              <Form.Item
                name="callForQuotation"
                label="Was there a call for quotation or expression of interest?"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Input placeholder="Give details" />
              </Form.Item>
              <Form.Item name="paymentTerms" label="Terms of payment" rules={[{ required: true, message: 'Payment terms are required' }]}>
                <Input placeholder="e.g. 30 days from invoice date" />
              </Form.Item>
              <Form.Item name="referenceNo" label="Reference and date of winning bid, contract or offer">
                <Input placeholder="If applicable" />
              </Form.Item>
              <Form.Item
                name="fundsAvailable"
                label="Availability and source of funds"
                rules={[{ required: true, message: 'Confirm the availability of funds' }]}
              >
                <Input placeholder="Confirm funds are available and where they come from" />
              </Form.Item>
              <Form.Item name="history" label="Two-year history of direct orders with this supplier">
                <TextArea rows={2} placeholder="Brief summary" />
              </Form.Item>
              <Form.Item name="approver" label="Perm Sec / Chairman approving" initialValue="Francis Agius" required>
                <Input disabled />
              </Form.Item>
            </Section>

            <Section title="Attachments" hint="Quotations, contracts or anything else that supports the request. Up to 3 files, 100 MB each.">
              <Form.Item style={{ marginBottom: 0 }}>
                <Upload
                  beforeUpload={() => false}
                  onChange={handleFileUpload}
                  multiple
                  listType="text"
                  accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,image/*,video/*,audio/*"
                  maxCount={3}
                >
                  <Button icon={<UploadOutlined />}>Choose files</Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Word, Excel, PowerPoint, PDF, images, video or audio.
                </Text>
              </Form.Item>
            </Section>
          </div>

          <aside className="do-rail" aria-label="Order summary">
            <div className="do-panel">
              <div className="do-panel__body">
                <h2 className="do-panel__title" style={{ marginBottom: 14 }}>Approval route</h2>
                <ApprovalTrack order={{}} variant="vertical" />
              </div>
              <div className="do-panel__body">
                <span className="do-fact__label">Total (excl. VAT)</span>
                <span className="do-total">{totalLabel}</span>
                <span className="do-fact__label" style={{ marginTop: 8 }}>
                  {attachments.length === 0 ? 'No attachments' : `${attachments.length} ${attachments.length === 1 ? 'attachment' : 'attachments'}`}
                </span>
              </div>
              <div className="do-panel__body do-submit-inline">
                <Button type="primary" htmlType="submit" size="large" icon={<SendOutlined />} loading={loading} block>
                  Submit direct order
                </Button>
                <Link to="/direct-order">
                  <Button type="text" block style={{ marginTop: 8 }}>
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <div className="do-submitbar">
          <div className="do-submitbar__total">
            Total (excl. VAT)
            <strong>{totalLabel}</strong>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/direct-order">
              <Button type="text">Cancel</Button>
            </Link>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={loading}>
              Submit
            </Button>
          </div>
        </div>
      </Form>

      <Modal
        title="Start from a previous order"
        open={previousOrdersModal}
        onCancel={() => setPreviousOrdersModal(false)}
        footer={null}
        width={900}
        rootClassName="do-root do-scope"
        destroyOnClose
      >
        <p className="do-muted" style={{ margin: '0 0 16px' }}>
          Pick an order to copy its content into the form. Your details, today's date and attachments are not copied.
        </p>
        <OrdersExplorer
          orders={userDirectOrders}
          loading={loading}
          hideGroupFilter
          hideInitiativeFilter
          onOpen={handleSelectPreviousOrder}
          renderActions={(order) => (
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleSelectPreviousOrder(order)}>
              Use
            </Button>
          )}
          emptyTitle="No previous orders"
          emptyHint="Orders you raise will be available here as templates."
        />
      </Modal>
    </DOLayout>
  );
};

export default CreateDirectOrder;
